import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { SafeAreaView, StyleSheet, Text, ScrollView, View, TouchableOpacity, ImageBackground, Platform } from 'react-native'
import { Heading, Input, Avatar, Box, FormControl, TextArea, Select, Icon, useToast, KeyboardAvoidingView } from 'native-base'
import LottieView from 'lottie-react-native'
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil'
import { userState } from '../state/atoms'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { collection, doc, getDoc, getDocs, updateDoc, onSnapshot } from 'firebase/firestore'
import { auth, db, storage } from '../firebase'
import UserPhoto from '../components/UserPhoto'
import { states, suffix, gender, industry } from '../utils/utils'
import { FontAwesome } from '@expo/vector-icons'
import { useNavigation } from '@react-navigation/core'
import * as ImagePicker from 'expo-image-picker'

const NameSection = styled(View)`
    flex-direction: column;
    justify-content: space-between;
`
const Names = styled(View)`
    flex-direction: row;
    justify-content: space-between;
`
const AdditionalNames = styled(View)`
    flex-direction: row;
    justify-content: space-between;
`
const BioSection = styled(View)``
const ContactSection = styled(View)`
    flex-direction: row;
    justify-content: space-between;
`
const AddressSection = styled(View)``
const Address = styled(View)`
    flex-direction: row;
    justify-content: space-between;
`
const CityStateZip = styled(View)`
    flex-direction: row;
    justify-content: space-between;
`
const CareerSection = styled(View)`
    flex-direction: row;
    justify-content: space-between;
`

const HeaderRow = styled(View)`
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`

const PhotoWrapper = styled(View)`
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: center;
`

const SocialMediaSection = styled(View)``

const SettingsScreen = () => {
    const userID = auth.currentUser.uid
    const toast = useToast()
    const navigation = useNavigation()
    const userData = useRecoilValue(userState)
    const resetUserData = useResetRecoilState(userState)
    const initials = userData?.first_name.split('')[0] + userData?.last_name.split('')[0]
    const [exitAnimation, setExitAnimation] = useState(false)
    const [userPhoto, setUserPhoto] = useState(userData?.user_photo)
    const [firstNameValue, setFirstNameValue] = useState(userData?.first_name)
    const [lastNameValue, setLastNameValue] = useState(userData?.last_name)
    const [suffixValue, setSuffixValue] = useState(userData?.suffix)
    const [genderValue, setGenderValue] = useState(userData?.gender)
    const [bioValue, setBioValue] = useState(userData?.bio)
    const [phoneValue, setPhoneValue] = useState(userData?.phone_number)
    const [addressValue, setAddressValue] = useState(userData?.location?.street_address)
    const [address2Value, setAddress2Value] = useState(userData?.location?.street_address_2)
    const [cityValue, setCityValue] = useState(userData?.location?.city)
    const [stateValue, setStateValue] = useState(userData?.location?.state)
    const [zipValue, setZipValue] = useState(userData?.location?.zip)
    const [industryValue, setIndustryValue] = useState(userData?.industry)
    const [jobTitleValue, setJobTitleValue] = useState(userData?.job_title)

    // Handle signing out of account
    const handleSignOut = () => {
        setExitAnimation(true)
        resetUserData()
        auth.signOut()
            .then(() => {
                setTimeout(() => {
                    navigation.replace('Login')
                    setExitAnimation(false)
                }, 5000)
            })
            .catch((error) => alert(error.message))
    }

    const handleUpdate = () => {
        const userRef = doc(db, 'users', userID)
        const data = {
            first_name: firstNameValue,
            last_name: lastNameValue,
            suffix: suffixValue,
            gender: genderValue,
            bio: bioValue,
            phone_number: phoneValue,
            location: {
                street_address: addressValue,
                street_address_2: address2Value,
                city: cityValue,
                state: stateValue,
                zip: zipValue,
            },
            industry: industryValue,
            job_title: jobTitleValue,
        }
        uploadFile(userPhoto)
        updateDoc(userRef, data).then(() => {
            toast.show({ description: 'Profile updated!', placement: 'top' })
        })
    }

    const handleUserPhotoClick = async () => {
        await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.All,
            allowsEditing: true,
            quality: 0.5,
        }).then((result) => {
            if (!result.cancelled) {
                setUserPhoto(result.uri)
            }
        })
    }

    const uploadFile = async (file) => {
        if (!file) return
        const storageRef = ref(storage, `/users/avatars/${userID}/${userData.username}`)
        let blob = await fetch(file).then((r) => r.blob())
        uploadBytes(storageRef, blob).then((snapshot) => {
            getDownloadURL(snapshot.ref).then((downloadURL) => {
                const userRef = doc(db, 'users', userID)
                updateDoc(userRef, { user_photo: downloadURL })
            })
        })
    }

    return exitAnimation ? (
        <>
            <View
                style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                }}
            >
                {Platform.OS !== 'web' && <LottieView source={require('../assets/animations/house.json')} autoPlay loop={false} />}
                <Heading style={{ marginTop: 500 }}>See you next time, hunter...</Heading>
                <Text>Securely logging you out</Text>
            </View>
        </>
    ) : (
        <ImageBackground style={{ width: '100%', height: '100%', alignSelf: 'center' }}>
            {/* <LottieView source={require('../assets/animations/particles.json')} style={{ opacity: 0.5 }} autoPlay loop /> */}
            <SafeAreaView style={styles.container}>
                <HeaderRow>
                    <Heading>Settings</Heading>
                    <TouchableOpacity onPress={handleSignOut}>
                        <FontAwesome name="sign-out" size={36} color="#6c387a" />
                    </TouchableOpacity>
                </HeaderRow>
                <ScrollView style={{ width: '100%' }}>
                    <PhotoWrapper>
                        <TouchableOpacity onPress={handleUserPhotoClick}>
                            <UserPhoto image={userPhoto} size={'xl'} />
                        </TouchableOpacity>
                    </PhotoWrapper>
                    <Box border="1" borderRadius="md" style={{ flex: 1 }}>
                        <Box px="4">
                            <NameSection>
                                <Names>
                                    <FormControl w="49%">
                                        <FormControl.Label isRequired>First Name:</FormControl.Label>
                                        <Input value={firstNameValue} onChangeText={(newValue) => setFirstNameValue(newValue)} />
                                    </FormControl>
                                    <FormControl w="49%">
                                        <FormControl.Label isRequired>Last Name:</FormControl.Label>
                                        <Input value={lastNameValue} onChangeText={(newValue) => setLastNameValue(newValue)} />
                                    </FormControl>
                                </Names>
                                <AdditionalNames>
                                    <FormControl w="49%">
                                        <FormControl.Label>Suffix:</FormControl.Label>
                                        <Select selectedValue={suffixValue} onValueChange={(selectedSuffix) => setSuffixValue(selectedSuffix)}>
                                            {suffix.map((type, idx) => {
                                                return <Select.Item key={idx} label={type} value={type} />
                                            })}
                                        </Select>
                                    </FormControl>
                                    <FormControl w="49%">
                                        <FormControl.Label>Gender:</FormControl.Label>
                                        <Select selectedValue={genderValue} onValueChange={(selectedGender) => setGenderValue(selectedGender)}>
                                            {gender.map((type, idx) => {
                                                return <Select.Item key={idx} label={type} value={type} />
                                            })}
                                        </Select>
                                    </FormControl>
                                </AdditionalNames>
                            </NameSection>
                            <BioSection>
                                <FormControl>
                                    <FormControl.Label>Bio:</FormControl.Label>
                                    <TextArea value={bioValue} onChangeText={(newValue) => setBioValue(newValue)} />
                                </FormControl>
                            </BioSection>
                            <ContactSection>
                                <FormControl w="55%">
                                    <FormControl.Label isRequired>Email:</FormControl.Label>
                                    <Input isReadOnly isDisabled>
                                        {userData?.email}
                                    </Input>
                                </FormControl>
                                <FormControl w="43%">
                                    <FormControl.Label>Phone Number:</FormControl.Label>
                                    <Input keyboardType="phone-number" value={phoneValue} onChangeText={(newValue) => setPhoneValue(newValue)} />
                                </FormControl>
                            </ContactSection>
                            <AddressSection>
                                <Address>
                                    <FormControl w="74%">
                                        <FormControl.Label>Street Address:</FormControl.Label>
                                        <Input value={addressValue} onChangeText={(newValue) => setAddressValue(newValue)} />
                                    </FormControl>
                                    <FormControl w="24%">
                                        <FormControl.Label>Apt/Suite:</FormControl.Label>
                                        <Input value={address2Value} onChangeText={(newValue) => setAddress2Value(newValue)} />
                                    </FormControl>
                                </Address>
                                <CityStateZip>
                                    <FormControl w="49%">
                                        <FormControl.Label>City:</FormControl.Label>
                                        <Input value={cityValue} onChangeText={(newValue) => setCityValue(newValue)} />
                                    </FormControl>
                                    <FormControl w="24%">
                                        <FormControl.Label>State:</FormControl.Label>
                                        <Select selectedValue={stateValue} onValueChange={(selectedState) => setStateValue(selectedState)}>
                                            {states.map((state, idx) => {
                                                return <Select.Item key={idx} label={state} value={state} />
                                            })}
                                        </Select>
                                    </FormControl>
                                    <FormControl w="24%">
                                        <FormControl.Label>Zip:</FormControl.Label>
                                        <Input type="number" keyboardType="number" value={zipValue} onChangeText={(newValue) => setZipValue(newValue)} />
                                    </FormControl>
                                </CityStateZip>
                            </AddressSection>
                            <CareerSection>
                                <FormControl w="49%">
                                    <FormControl.Label>Industry:</FormControl.Label>
                                    <Select selectedValue={industryValue} onValueChange={(selectedIndustry) => setIndustryValue(selectedIndustry)}>
                                        {industry.map((type, idx) => {
                                            return <Select.Item key={idx} label={type} value={type} />
                                        })}
                                    </Select>
                                </FormControl>
                                <FormControl w="49%">
                                    <FormControl.Label>Job Title:</FormControl.Label>
                                    <Input value={jobTitleValue} onChangeText={(newValue) => setJobTitleValue(newValue)} />
                                </FormControl>
                            </CareerSection>
                            {/* <SocialMediaSection>
                            <FormControl>
                                <FormControl.Label>Facebook:</FormControl.Label>
                                <Input>{userData?.social_media?.facebook}</Input>
                            </FormControl>
                            <FormControl>
                                <FormControl.Label>Twitter Handle:</FormControl.Label>
                                <Input InputLeftElement={<Icon as={<FontAwesome name="at" />} size="sm" m={2} color="#6c387a" />}>{userData?.social_media?.twitter}</Input>
                            </FormControl>
                            <FormControl>
                                <FormControl.Label>Linkedin Name:</FormControl.Label>
                                <Input>{userData?.social_media?.linkedin}</Input>
                            </FormControl>
                        </SocialMediaSection> */}
                        </Box>
                        <Box px="4" pb="4"></Box>
                    </Box>
                    <View>
                        <TouchableOpacity style={styles.button} onPress={handleUpdate}>
                            <Text style={styles.buttonText}>Update</Text>
                        </TouchableOpacity>
                    </View>
                </ScrollView>
            </SafeAreaView>
        </ImageBackground>
    )
}

export default SettingsScreen

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        margin: 20,
    },
    button: {
        backgroundColor: '#6c387a',
        width: '100%',
        padding: 15,
        borderRadius: 5,
        alignItems: 'center',
        marginTop: 20,
    },
    buttonText: {
        color: 'white',
        fontWeight: '700',
        fontSize: 16,
    },
})
